import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import type { ITheme } from '@inter-ikea-kompis/types';
import { IframeUtility, IframeVisibleAreaObserver } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Collapsible } from '@inter-ikea-kompis/component-collapsible';
import { HyperlinkColorEnum, HyperlinkTargetEnum } from '@inter-ikea-kompis/component-hyperlink';
import { Hyperlink } from '@inter-ikea-kompis/component-hyperlink';
import { Icon } from '@inter-ikea-kompis/component-icon';
import { Text } from '@inter-ikea-kompis/component-text';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';
import { crossSmall, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';

import ToastAnimationNameEnum from '../../enums/ToastAnimationNameEnum.js';
import ToastFocusUtil from '../../utils/ToastFocusUtil.js';
import ToastStyle from './ToastStyle.js';

const TIMEOUT_TIME = {
	min: 5000,
	max: 10000,
	factor: 50
};

/**
 * @deprecated This component has been deprecated, see Kompis page for more info.
 *
 * This component should be used for general toasts and it provides with general stylings according to NCL and fixes for common browsers and touch devices.
 *
 *
 * @event close Triggered by user or timeout.
 * @event animationEnd When the animation for opening/closing ends.
 * @event actionClick When user clicks the action
 */

export default class Toast extends KompisElement {
	// Visible: Show or hide toast.
	@property({ type: Boolean })
	public visible = false;

	// Title on toast.
	@property({ type: String })
	public label: string | null = null;

	// Label for action.
	@property({ type: String })
	public actionLabel: string | null = null;

	// Href for action.
	@property({ type: String })
	public actionHref: string | null = null;

	// The same as target for a <a>-tag
	@property({ type: String })
	public actionTarget: HyperlinkTargetEnum = HyperlinkTargetEnum.blank;

	// Label for the close button to set as an aria-label for accessibility reasons
	@property({ type: String })
	public closeButtonAriaLabel: string | null = null;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	private isAnimating = false;

	private toastFocusUtil = new ToastFocusUtil(this.onFocusChange.bind(this));
	private timerId: ReturnType<typeof setTimeout> | null = null;
	private iframeVisibleAreaObserver =
		IframeUtility.isInsideIframe() && IframeUtility.isParentWindowAccessible()
			? new IframeVisibleAreaObserver({
					debounceTime: 100,
					overflow: true,
					callback: () => {
						this.requestUpdate();
					}
				})
			: null;

	public override render(): RenderResult {
		const area = this.iframeVisibleAreaObserver
			? this.iframeVisibleAreaObserver.getArea()
			: { top: 0, bottom: 0 };

		if (!this.visible && !this.isAnimating) {
			return null;
		}

		return html`
			<style>
				${ToastStyle.getStyle({
					isVisible: this.visible,
					theme: this.theme,
					top: area.top,
					bottom: area.bottom,
					direction: window.getComputedStyle(this).direction,
					isAnimating: this.isAnimating
				})}
			</style>
			<div
				class="toastContainer"
				role="alert"
				aria-live="assertive"
				aria-atomic="false"
				@animationend="${this.onAnimationEnd.bind(this)}"
			>
				${this.getToastContent()}
				<div class="containerSpace"></div>
			</div>
		`;
	}

	public override async connectedCallback(): Promise<void> {
		registerIcon(crossSmall);

		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('skapa-icon', SkapaIcon);

		this.customElements.define('kompis-icon', Icon);
		this.customElements.define('kompis-text', Text);
		this.customElements.define('kompis-hyperlink', Hyperlink);
		this.customElements.define('kompis-collapsible', Collapsible);
		super.connectedCallback();
		window.addEventListener('keydown', this.onKeyDownListener);
	}

	public animateChange(): void {
		const element = this.shadowRoot?.querySelector('.toast');
		if (element && 'animate' in element) {
			element.animate(
				[
					{
						transform: 'none',
						opacity: 1
					},
					{
						transform: 'scale(0.8)',
						opacity: 0
					},
					{
						transform: 'none',
						opacity: 1
					}
				],
				{
					duration: Number(this.theme.motion.duration.medium.replace('ms', '')),
					easing: this.theme.motion.ease.bob
				}
			);
		}
	}

	public startTimer(): void {
		if (this.timerId) {
			this.stopTimer();
		}
		this.timerId = setTimeout(() => {
			this.timerId = null;
			this.dispatchEvent(
				new CustomEvent('close', {
					bubbles: true,
					detail: {
						type: 'timeout'
					}
				})
			);
		}, this.getTimeoutTime());
	}

	public stopTimer(): void {
		if (this.timerId) {
			clearTimeout(this.timerId);
			this.timerId = null;
		}
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.disconnect();
		this.isAnimating = false;
		window.removeEventListener('keydown', this.onKeyDownListener);
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('visible')) {
			this.isAnimating = true;
			if (this.visible) {
				this.toastFocusUtil.observe(this);
				if (this.iframeVisibleAreaObserver) {
					this.iframeVisibleAreaObserver.observe();
				}
				this.startTimer();
			} else {
				this.disconnect();
			}
		}
		return true;
	}

	/**
	 * The intersection between iframe and visible area.
	 *
	 * @returns - Html.
	 */
	private getToastContent(): RenderResult {
		return html`<div class="toast">
			<div class="body">
				<kompis-text
					.theme="${this.theme}"
					.type="${ThemeFontStyleTypeEnum.bodySmall}"
					class="message"
					>${this.label}</kompis-text
				>
				${this.getActionButton()}
			</div>
			${this.actionLabel ? html`<div class="stripe"></div>` : null}
			<div class="space-25"></div>
			${this.getCloseButton()}
		</div>`;
	}

	private disconnect(): void {
		this.stopTimer();
		this.toastFocusUtil.disconnect();
		if (this.iframeVisibleAreaObserver) {
			this.iframeVisibleAreaObserver.disconnect();
		}
	}

	private onFocusChange(hasFocus: boolean): void {
		if (hasFocus) {
			this.stopTimer();
		} else {
			this.startTimer();
		}
	}

	/**
	 * Returns close button.
	 *
	 * @returns Template.
	 */
	private getActionButton(): RenderResult {
		if (!this.actionLabel) {
			return null;
		}

		return html`
			<span @animationend="${this.onAnimationEnd.bind(this)}">
				<kompis-text
					.theme="${this.theme}"
					.bold="${true}"
					.type="${ThemeFontStyleTypeEnum.bodySmall}"
				>
					<kompis-hyperlink
						@linkClick="${() =>
							this.dispatchEvent(new CustomEvent('actionClick', { bubbles: true }))}"
						class="action"
						.href="${this.actionHref}"
						.target="${this.actionTarget}"
						.color="${HyperlinkColorEnum.white}"
						.text="${this.actionLabel}"
						.theme="${this.theme}"
					></kompis-hyperlink>
				</kompis-text>
			</span>
		`;
	}

	/**
	 * Returns close button.
	 *
	 * @returns Template.
	 */
	private getCloseButton(): RenderResult {
		const tertiaryVariant: ButtonVariant = 'tertiary';

		// The class 'skapa-focus-portal' is used to be able to click "close" button when toast is above a modal.
		return html`
			<div class="close skapa-focus-portal">
				<skapa-icon-button
					@click="${() => this.onCloseButtonClick()}"
					inverse
					size="xsmall"
					variant="${tertiaryVariant}"
				>
					<skapa-icon slot="icon" icon="cross-small"></skapa-icon>
					${this.closeButtonAriaLabel}
				</skapa-icon-button>
			</div>
		`;
	}

	/**
	 * Handles close button click.
	 */
	private onCloseButtonClick(): void {
		this.dispatchEvent(
			new CustomEvent('close', {
				bubbles: true,
				detail: {
					type: 'click'
				}
			})
		);
	}

	/**
	 * Handles animation end.
	 *
	 * @param event ...
	 */
	private onAnimationEnd(event: AnimationEvent): void {
		if (event.animationName.startsWith(ToastAnimationNameEnum.closing)) {
			this.isAnimating = false;
			this.dispatchEvent(
				new CustomEvent('animationEnd', {
					bubbles: true,
					detail: {
						type: 'close'
					}
				})
			);
		} else if (event.animationName.startsWith(ToastAnimationNameEnum.opening)) {
			this.dispatchEvent(
				new CustomEvent('animationEnd', {
					bubbles: true,
					detail: {
						type: 'open'
					}
				})
			);
		}
		this.update();
	}

	/**
	 * Closes sheet on escape.
	 *
	 * @param event Event.
	 */
	private onKeyDownListener = (event: KeyboardEvent): void => {
		if (event.code === 'Escape' && this.visible) {
			this.dispatchEvent(new CustomEvent('close', { bubbles: true, detail: { type: 'escape' } }));
		}
	};

	private getTimeoutTime(): number {
		const numberOfCharacters: number = (this.label?.length || 0) + (this.actionLabel?.length || 0);
		const calculatedTime = numberOfCharacters * TIMEOUT_TIME.factor;
		if (calculatedTime > TIMEOUT_TIME.max) {
			return TIMEOUT_TIME.max;
		}
		if (calculatedTime < TIMEOUT_TIME.min) {
			return TIMEOUT_TIME.min;
		}
		return calculatedTime;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'skapa-icon': SkapaIcon;
		'kompis-toast': Toast;
	}
}
